/**
 * Global prefetch hook — primes React Query cache with data commonly needed
 * by authenticated pages so navigation feels instant.
 *
 * Called once in App.tsx. Each prefetch is gated by staleTime so it only
 * makes a network request when the cached data has expired.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { fetchProjects } from '@/hooks/queries/useProjects';

const ARTIE_STALE_TIME = 1000 * 60 * 30; // 30 minutes — matches ArtieAssistant.tsx

export function useAppPrefetch() {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return;

    // Prefetch the project list used by the Artie Assistant sidebar.
    // Uses the exact same queryKey shape as ArtieAssistant.tsx so the cache hit is guaranteed.
    const filters = {
      view: isAdmin ? undefined : ('public' as const),
      limit: 500,
      page: 1,
    };

    queryClient.prefetchQuery({
      queryKey: ['projects', 'v2', filters],
      queryFn: () => fetchProjects(filters, 'v2'),
      staleTime: ARTIE_STALE_TIME,
    });
  }, [user, isAdmin, queryClient]);
}
